import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import './Recruitment.css';

const APPLICATIONS_PER_PAGE = 50;

const STATUS_OPTIONS = [
    ['pending', 'Pending'], ['under_review', 'Under Review'], ['shortlisted', 'Shortlisted'], ['not_shortlisted', 'Not Shortlisted'],
    ['interviewed', 'Interviewed'], ['accepted', 'Accepted'], ['rejected', 'Rejected'],
];

const Recruitment = () => {
    const { t } = useLanguage();
    const { hasPermission } = useAuth();
    // recruitment.manage: everything; recruitment.review: view applications and change their status.
    const canManage = hasPermission('recruitment.manage');
    const canReview = canManage || hasPermission('recruitment.review');
    const [selectedIds, setSelectedIds] = useState([]);
    const [allMatching, setAllMatching] = useState(false);
    const [bulkStatus, setBulkStatus] = useState('');
    const [bulkSaving, setBulkSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('jobs');
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState([]);
    const [appPage, setAppPage] = useState(1);
    const [appStatus, setAppStatus] = useState('');
    const [appSearch, setAppSearch] = useState('');
    const latestLoad = useRef(0); // ignore responses that arrive after a newer search
    const [jobFilter, setJobFilter] = useState('');
    const [appPagination, setAppPagination] = useState({ total: 0, total_pages: 1 });
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showJobForm, setShowJobForm] = useState(false);
    const [showApplicationForm, setShowApplicationForm] = useState(false);
    const [editingJob, setEditingJob] = useState(null);
    const [viewingApplication, setViewingApplication] = useState(null);
    const [jobForm, setJobForm] = useState({
        title: '', description: '', requirements: '', responsibilities: '', qualifications: '',
        location: '', employment_type: 'full_time', salary_range: '', posted_date: new Date().toISOString().split('T')[0],
        deadline: '', status: 'draft'
    });
    const [applicationForm, setApplicationForm] = useState({
        job_posting_id: '', applicant_name: '', applicant_email: '', applicant_phone: '', cover_letter: '', resume: null
    });

    useEffect(() => {
        loadJobs();
        loadApplications();
        loadStatistics();
    }, []);

    const loadJobs = async () => {
        setLoading(true);
        try {
            const res = await api.getJobs({ per_page: 100 });
            setJobs(res.data || []);
        } catch (err) {
            console.error('Failed to load jobs', err);
        } finally {
            setLoading(false);
        }
    };

    // Live search: reload page 1 shortly after typing stops.
    const firstSearchRun = useRef(true);
    useEffect(() => {
        if (firstSearchRun.current) { firstSearchRun.current = false; return undefined; }
        const timer = setTimeout(() => loadApplications(1, appStatus, appSearch), 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appSearch]);

    const loadApplications = async (page = appPage, status = appStatus, search = appSearch) => {
        const requestId = ++latestLoad.current;
        try {
            const params = { page, per_page: APPLICATIONS_PER_PAGE };
            if (status) params.status = status;
            if (search.trim()) params.search = search.trim();
            const res = await api.getApplications(params);
            if (requestId !== latestLoad.current) return;
            setApplications(res.data || []);
            setSelectedIds([]);
            setAllMatching(false);
            setAppPagination(res.pagination || { total: (res.data || []).length, total_pages: 1 });
            setAppPage(page);
            setAppStatus(status);
        } catch (err) {
            console.error('Failed to load applications', err);
        }
    };

    const loadStatistics = async () => {
        try {
            const res = await api.getRecruitmentStatistics();
            setStatistics(res.data);
        } catch (err) {
            console.error('Failed to load statistics', err);
        }
    };

    const openJobForm = (job = null) => {
        if (job) {
            setEditingJob(job);
            setJobForm({
                title: job.title || '',
                description: job.description || '',
                requirements: job.requirements || '',
                responsibilities: job.responsibilities || '',
                qualifications: job.qualifications || '',
                location: job.location || '',
                employment_type: job.employment_type || 'full_time',
                salary_range: job.salary_range || '',
                posted_date: job.posted_date || new Date().toISOString().split('T')[0],
                deadline: job.deadline || '',
                status: job.status || 'draft'
            });
        } else {
            setEditingJob(null);
            setJobForm({
                title: '', description: '', requirements: '', responsibilities: '', qualifications: '',
                location: '', employment_type: 'full_time', salary_range: '', posted_date: new Date().toISOString().split('T')[0],
                deadline: '', status: 'draft'
            });
        }
        setShowJobForm(true);
    };

    const closeJobForm = () => {
        setShowJobForm(false);
        setEditingJob(null);
    };

    const handleJobSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingJob) {
                await api.updateJob(editingJob.id, jobForm);
            } else {
                await api.createJob(jobForm);
            }
            closeJobForm();
            loadJobs();
            loadStatistics();
        } catch (err) {
            console.error('Save job failed', err);
            alert(err.message || t('Failed to save job'));
        }
    };

    const handleDeleteJob = async (id) => {
        if (!window.confirm(t('Je, una uhakika unataka kufuta tangazo hili la kazi?'))) return;
        try {
            await api.deleteJob(id);
            loadJobs();
            loadStatistics();
        } catch (err) {
            console.error('Delete job failed', err);
            alert(err.message || t('Failed to delete job'));
        }
    };

    const openApplicationForm = (jobId = null) => {
        setApplicationForm({
            job_posting_id: jobId || '',
            applicant_name: '',
            applicant_email: '',
            applicant_phone: '',
            cover_letter: '',
            resume: null
        });
        setShowApplicationForm(true);
    };

    const closeApplicationForm = () => {
        setShowApplicationForm(false);
        setApplicationForm({ job_posting_id: '', applicant_name: '', applicant_email: '', applicant_phone: '', cover_letter: '', resume: null });
    };

    const handleApplicationSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('job_posting_id', applicationForm.job_posting_id);
            formData.append('applicant_name', applicationForm.applicant_name);
            formData.append('applicant_email', applicationForm.applicant_email);
            formData.append('applicant_phone', applicationForm.applicant_phone);
            if (applicationForm.cover_letter) formData.append('cover_letter', applicationForm.cover_letter);
            if (applicationForm.resume) formData.append('resume', applicationForm.resume);

            await api.submitApplication(formData);
            closeApplicationForm();
            loadApplications();
            loadStatistics();
            alert(t('Application submitted successfully!'));
        } catch (err) {
            console.error('Submit application failed', err);
            alert(err.message || t('Failed to submit application'));
        }
    };

    const handleStatusUpdate = async (applicationId, newStatus, reviewNotes = '') => {
        try {
            await api.updateApplicationStatus(applicationId, { status: newStatus, review_notes: reviewNotes });
            loadApplications();
            loadStatistics();
            alert(t('Application status updated'));
            return true;
        } catch (err) {
            console.error('Update status failed', err);
            alert(err.message || t('Failed to update status'));
            return false;
        }
    };

    const pageIds = applications.map((app) => app.id);
    const pageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    const selectedCount = allMatching ? appPagination.total : selectedIds.length;
    const toggleSelected = (id) => {
        setAllMatching(false);
        setSelectedIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
    };
    const togglePage = () => {
        setAllMatching(false);
        setSelectedIds(pageSelected ? [] : pageIds);
    };
    const clearSelection = () => { setSelectedIds([]); setAllMatching(false); setBulkStatus(''); };

    const handleBulkStatus = async () => {
        if (!bulkStatus || !selectedCount) return;
        const label = t(statusLabels[bulkStatus] || bulkStatus);
        if (!window.confirm(`${t('Change the status of')} ${selectedCount} ${t('applications to')} "${label}"?`)) return;
        setBulkSaving(true);
        try {
            const payload = allMatching ? { all: true, filter_status: appStatus, search: appSearch.trim(), status: bulkStatus } : { ids: selectedIds, status: bulkStatus };
            const res = await api.bulkUpdateApplicationStatus(payload);
            clearSelection();
            await loadApplications(appPage, appStatus);
            loadStatistics();
            alert(`${res.data?.updated ?? 0} ${t('applications updated.')}`);
        } catch (err) {
            alert(err.message || t('Failed to update status'));
        } finally {
            setBulkSaving(false);
        }
    };

    const openApplication = async (id) => {
        try {
            const res = await api.getApplication(id);
            setViewingApplication(res.data);
        } catch (err) {
            console.error('Load application failed', err);
            alert(t('Failed to load application'));
        }
    };

    const changeViewingStatus = async (status) => {
        const id = viewingApplication.id;
        if (!(await handleStatusUpdate(id, status))) return;
        setViewingApplication((current) => (current && current.id === id ? { ...current, status } : current));
    };

    const openDocument = async (key, name) => {
        try {
            await api.openApplicationDocument(viewingApplication.id, key, name);
        } catch (err) {
            console.error('Open document failed', err);
            alert(t('Document could not be opened.'));
        }
    };

    const statusLabels = {
        pending: 'Pending', under_review: 'Under Review', shortlisted: 'Shortlisted', not_shortlisted: 'Not Shortlisted', interviewed: 'Interviewed',
        accepted: 'Accepted', rejected: 'Rejected', withdrawn: 'Withdrawn'
    };

    const documentLabels = {
        application_letter: t('Application Letter'),
        academic_document: t('Academic Certificates'),
        driver_document: t('Driving Licence / Trade Test')
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('sw-TZ', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getStatusColor = (status) => {
        const colors = {
            draft: '#888', open: '#4CAF50', closed: '#f44336', cancelled: '#ff9800',
            pending: '#2196F3', under_review: '#FF9800', shortlisted: '#9C27B0', not_shortlisted: '#795548',
            interviewed: '#673AB7', accepted: '#4CAF50', rejected: '#f44336', withdrawn: '#888'
        };
        return colors[status] || '#888';
    };

    const statItems = statistics ? [
        { value: statistics.total_jobs, label: t('Jumla ya Matangazo'), icon: '▣', tone: 'ink', tab: 'jobs', filter: '' },
        { value: statistics.open_jobs, label: t('Zilizo Wazi'), icon: '↗', tone: 'green', tab: 'jobs', filter: 'open' },
        { value: statistics.total_applications, label: t('Jumla ya Maombi'), icon: '☷', tone: 'blue', tab: 'applications', filter: '' },
        { value: statistics.pending_applications, label: t('Inasubiri'), icon: '◷', tone: 'orange', tab: 'applications', filter: 'pending' },
        { value: statistics.shortlisted, label: t('Waliochaguliwa'), icon: '★', tone: 'yellow', tab: 'applications', filter: 'shortlisted' }
    ] : [];

    const openStat = (item) => {
        setActiveTab(item.tab);
        if (item.tab === 'jobs') setJobFilter(item.filter);
        else loadApplications(1, item.filter);
        document.querySelector('.content-toolbar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const isStatActive = (item) => activeTab === item.tab && (item.tab === 'jobs' ? jobFilter : appStatus) === item.filter;

    const visibleJobs = jobFilter ? jobs.filter(job => job.status === jobFilter) : jobs;

    return (
        <div className="recruitment-page">
            <div className="page-header recruitment-hero">
                <div className="header-left">
                    <span className="hero-kicker">TAREWU • HR WORKSPACE</span>
                    <h1 className="page-title">{t('Matangazo ya Ajira')}</h1>
                    <p className="page-subtitle">{t('Dhibiti matangazo ya kazi na maombi')}</p>
                </div>
                <div className="header-actions">
                    <button className="btn btn-secondary hero-secondary" onClick={() => openApplicationForm()}><span>+</span>{t('Ongeza Ombi')}</button>
                    {canManage && <button className="btn btn-primary hero-primary" onClick={() => openJobForm()}><span>+</span>{t('Tangazo Jipya')}</button>}
                </div>
            </div>

            {statistics && (
                <div className="statistics-grid">
                    {statItems.map((item) => (
                        <button type="button" className={`stat-card ${item.tone}${isStatActive(item) ? ' active' : ''}`} key={item.label} onClick={() => openStat(item)}>
                            <div className="stat-icon" aria-hidden="true">{item.icon}</div>
                            <div className="stat-copy">
                                <div className="stat-label">{item.label}</div>
                                <div className="stat-value">{item.value ?? 0}</div>
                            </div>
                            <span className="stat-arrow" aria-hidden="true">›</span>
                        </button>
                    ))}
                </div>
            )}

            <div className="content-toolbar">
              <div className="tabs">
                <button className={activeTab === 'jobs' ? 'tab active' : 'tab'} onClick={() => setActiveTab('jobs')}>
                    {t('Matangazo ya Kazi')} <b>{visibleJobs.length}</b>
                </button>
                <button className={activeTab === 'applications' ? 'tab active' : 'tab'} onClick={() => setActiveTab('applications')}>
                    {t('Maombi')} <b>{appPagination.total ?? applications.length}</b>
                </button>
              </div>
              {(activeTab === 'jobs' ? jobFilter : appStatus) ? (
                <button type="button" className="filter-chip" onClick={() => activeTab === 'jobs' ? setJobFilter('') : loadApplications(1, '')}>
                    {t('Kichujio')}: {activeTab === 'jobs' ? t('Zilizo Wazi') : t(statusLabels[appStatus] || appStatus)} <span aria-hidden="true">✕</span>
                </button>
              ) : (
                <span className="toolbar-note">{activeTab === 'jobs' ? t('Nafasi zote za kazi') : t('Waombaji wote')}</span>
              )}
            </div>

            {activeTab === 'jobs' && (
                <div className="jobs-list">
                    {loading ? (
                        <div className="loading-spinner">{t('Loading...')}</div>
                    ) : visibleJobs.length > 0 ? (
                        visibleJobs.map(job => (
                            <div key={job.id} className="job-card">
                                <div className="job-header">
                                    <div className="job-heading">
                                        <span className="job-mark">{(job.title || 'J').charAt(0)}</span>
                                        <div>
                                            <small>{job.reference_number || t('Tangazo la ndani')}</small>
                                            <h3 className="job-title">{job.title}</h3>
                                        </div>
                                    </div>
                                    <span className="job-status" style={{ backgroundColor: getStatusColor(job.status) }}>
                                        {job.status}
                                    </span>
                                </div>
                                <div className="job-meta">
                                    <span>◉ {job.location || 'N/A'}</span>
                                    <span>◷ {job.employment_type?.replace('_', ' ') || 'N/A'}</span>
                                    <span>◇ {job.salary_range || t('Haijabainishwa')}</span>
                                </div>
                                <p className="job-description">{job.description?.substring(0, 200)}...</p>
                                <div className="job-insights">
                                    <span><small>{t('Mwisho wa Maombi')}</small><b>{formatDate(job.deadline)}</b></span>
                                    <span><small>{t('Maombi')}</small><b>{job.applications_count || 0}</b></span>
                                    <span><small>{t('Views')}</small><b>{job.views_count || 0}</b></span>
                                </div>
                                <div className="job-actions">
                                    {canManage && <button className="btn-sm btn-primary" onClick={() => openJobForm(job)}>{t('Hariri')}</button>}
                                    <button className="btn-sm btn-secondary" onClick={() => openApplicationForm(job.id)}>{t('Omba')}</button>
                                    {canManage && <button className="btn-sm btn-danger" onClick={() => handleDeleteJob(job.id)}>{t('Futa')}</button>}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="no-data">{t('Hakuna matangazo ya kazi.')}</p>
                    )}
                </div>
            )}

            {activeTab === 'applications' && (
                <form className="applications-search" role="search" onSubmit={(e) => e.preventDefault()}>
                    <input type="search" value={appSearch} onChange={(e) => setAppSearch(e.target.value)}
                        placeholder={t('Search by name, application number, email or phone')} aria-label={t('Search applicants')} />
                    {appSearch && <button type="button" className="btn btn-secondary" onClick={() => setAppSearch('')}>{t('Clear')}</button>}
                </form>
            )}

            {activeTab === 'applications' && (
                <div className="applications-list">
                    {canReview && applications.length > 0 && (
                        <div className={`bulk-status-bar ${selectedCount ? 'active' : ''}`}>
                            <label className="bulk-select-page">
                                <input type="checkbox" checked={pageSelected} onChange={togglePage} />
                                <span>{selectedCount ? `${selectedCount} ${t('selected')}` : t('Select applications to change their status together')}</span>
                            </label>
                            {pageSelected && !allMatching && appPagination.total > applications.length && (
                                <button type="button" className="bulk-link" onClick={() => setAllMatching(true)}>
                                    {t('Select all')} {appPagination.total}{appStatus ? ` (${t(statusLabels[appStatus] || appStatus)})` : ''}{appSearch.trim() ? ` · "${appSearch.trim()}"` : ''}
                                </button>
                            )}
                            {allMatching && <button type="button" className="bulk-link" onClick={clearSelection}>{t('Clear selection')}</button>}
                            {selectedCount > 0 && (
                                <div className="bulk-actions">
                                    <select className="status-select" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} aria-label={t('New status')}>
                                        <option value="">{t('New status…')}</option>
                                        {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{t(label)}</option>)}
                                    </select>
                                    <button type="button" className="btn btn-primary" disabled={!bulkStatus || bulkSaving} onClick={handleBulkStatus}>
                                        {bulkSaving ? t('Updating…') : t('Change status')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {applications.length > 0 ? (
                        <div className="table-scroll"><table className="data-table">
                            <thead>
                                <tr>
                                    {canReview && <th className="select-col"><input type="checkbox" aria-label={t('Select all on this page')} checked={pageSelected} onChange={togglePage} /></th>}
                                    <th>{t('Jina')}</th>
                                    <th>{t('Tangazo')}</th>
                                    <th>Email</th>
                                    <th>{t('Simu')}</th>
                                    <th>{t('Tarehe')}</th>
                                    <th>{t('Hali')}</th>
                                    <th>{t('Vitendo')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {applications.map(app => (
                                    <tr key={app.id} className={allMatching || selectedIds.includes(app.id) ? 'row-selected' : ''}>
                                        {canReview && <td className="select-col"><input type="checkbox" aria-label={app.applicant_name} checked={allMatching || selectedIds.includes(app.id)} onChange={() => toggleSelected(app.id)} /></td>}
                                        <td>{app.applicant_name}</td>
                                        <td>{app.job_title}</td>
                                        <td>{app.applicant_email}</td>
                                        <td>{app.applicant_phone}</td>
                                        <td>{formatDate(app.applied_date)}</td>
                                        <td>
                                            <span className="status-badge" style={{ backgroundColor: getStatusColor(app.status) }}>
                                                {app.status}
                                            </span>
                                        </td>
                                        <td>
                                            <button className="btn btn-sm btn-primary" onClick={() => openApplication(app.id)}>{t('View')}</button>
                                            {canReview && <select
                                                className="status-select"
                                                value={app.status}
                                                onChange={(e) => handleStatusUpdate(app.id, e.target.value)}
                                            >
                                                <option value="pending">{t('Pending')}</option>
                                                <option value="under_review">{t('Under Review')}</option>
                                                <option value="shortlisted">{t('Shortlisted')}</option>
                                                <option value="not_shortlisted">{t('Not Shortlisted')}</option>
                                                <option value="interviewed">{t('Interviewed')}</option>
                                                <option value="accepted">{t('Accepted')}</option>
                                                <option value="rejected">{t('Rejected')}</option>
                                            </select>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table></div>
                    ) : null}
                    {applications.length > 0 && (
                        <ul className="application-cards">
                            {applications.map(app => (
                                <li key={app.id} className={`application-card ${allMatching || selectedIds.includes(app.id) ? 'selected' : ''}`}>
                                    {canReview && (
                                        <label className="application-card-check">
                                            <input type="checkbox" checked={allMatching || selectedIds.includes(app.id)} onChange={() => toggleSelected(app.id)} />
                                            <span>{t('Select')}</span>
                                        </label>
                                    )}
                                    <button type="button" className="application-card-main" onClick={() => openApplication(app.id)}>
                                        <span className="application-card-avatar" aria-hidden="true">{(app.applicant_name || '?').trim().charAt(0).toUpperCase()}</span>
                                        <span className="application-card-text">
                                            <b>{app.applicant_name}</b>
                                            <small>{app.job_title}</small>
                                            <small className="application-card-meta">{app.application_number ? `${app.application_number} · ` : ''}{formatDate(app.applied_date)}</small>
                                        </span>
                                        <span className="status-badge" style={{ backgroundColor: getStatusColor(app.status) }}>
                                            {t(statusLabels[app.status] || app.status)}
                                        </span>
                                    </button>
                                    <div className="application-card-contact">
                                        <a href={`tel:${app.applicant_phone}`}>📞 {app.applicant_phone}</a>
                                        <a href={`mailto:${app.applicant_email}`}>✉ {app.applicant_email}</a>
                                    </div>
                                    <div className="application-card-actions">
                                        <button className="btn btn-sm btn-primary" onClick={() => openApplication(app.id)}>{t('View')}</button>
                                        {canReview && <select
                                            className="status-select"
                                            aria-label={t('Hali')}
                                            value={app.status}
                                            onChange={(e) => handleStatusUpdate(app.id, e.target.value)}
                                        >
                                            <option value="pending">{t('Pending')}</option>
                                            <option value="under_review">{t('Under Review')}</option>
                                            <option value="shortlisted">{t('Shortlisted')}</option>
                                            <option value="not_shortlisted">{t('Not Shortlisted')}</option>
                                            <option value="interviewed">{t('Interviewed')}</option>
                                            <option value="accepted">{t('Accepted')}</option>
                                            <option value="rejected">{t('Rejected')}</option>
                                        </select>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {applications.length === 0 && (
                        <p className="no-data">{t('Hakuna maombi ya kazi.')}</p>
                    )}
                    {appPagination.total_pages > 1 && (
                        <div className="pagination">
                            <button className="btn btn-sm" disabled={appPage <= 1} onClick={() => loadApplications(appPage - 1)}>‹ {t('Iliyotangulia')}</button>
                            <span>
                                {t('Ukurasa')} {appPage} / {appPagination.total_pages}
                                {' · '}{(appPage - 1) * APPLICATIONS_PER_PAGE + 1}–{(appPage - 1) * APPLICATIONS_PER_PAGE + applications.length} {t('kati ya')} {appPagination.total}
                            </span>
                            <button className="btn btn-sm" disabled={appPage >= appPagination.total_pages} onClick={() => loadApplications(appPage + 1)}>{t('Inayofuata')} ›</button>
                        </div>
                    )}
                </div>
            )}

            {/* Application Detail Modal */}
            {viewingApplication && (() => {
                const app = viewingApplication;
                const documents = [
                    ...(app.resume_path ? [{ key: 'resume', label: 'CV', name: `CV-${app.application_number}.${app.resume_path.split('.').pop()}` }] : []),
                    ...(app.additional_documents || []).map((doc, index) => ({ key: index, label: documentLabels[doc.category] || doc.category, name: doc.name })),
                ];
                const extension = (name) => (String(name).split('.').pop() || '').slice(0, 4).toUpperCase();
                return (
                    <div className="modal-overlay app-view-overlay" onClick={() => setViewingApplication(null)}>
                        <div className="modal-content app-view" role="dialog" aria-modal="true" aria-label={app.applicant_name} onClick={(e) => e.stopPropagation()}>
                            <header className="app-view-header">
                                <span className="app-view-avatar" aria-hidden="true">{(app.applicant_name || '?').trim().charAt(0).toUpperCase()}</span>
                                <div className="app-view-title">
                                    <h2>{app.applicant_name}</h2>
                                    <p>{app.job_title}</p>
                                    <span className="status-badge" style={{ backgroundColor: getStatusColor(app.status) }}>{t(statusLabels[app.status] || app.status)}</span>
                                </div>
                                <button className="app-view-close" aria-label={t('Close')} onClick={() => setViewingApplication(null)}>×</button>
                            </header>

                            <div className="app-view-body">
                                <div className="app-view-quick">
                                    <a href={`tel:${app.applicant_phone}`}><span aria-hidden="true">📞</span>{t('Call')}</a>
                                    <a href={`mailto:${app.applicant_email}`}><span aria-hidden="true">✉</span>Email</a>
                                </div>

                                <div className="app-view-facts">
                                    <div><small>{t('Application Number')}</small><b>{app.application_number}</b></div>
                                    <div><small>{t('Tarehe')}</small><b>{formatDate(app.applied_date)}</b></div>
                                    <div><small>{t('Simu')}</small><b>{app.applicant_phone}</b></div>
                                    <div><small>Email</small><b>{app.applicant_email}</b></div>
                                </div>

                                {app.cover_letter && (
                                    <section className="app-view-section">
                                        <h3>{t('Cover Letter')}</h3>
                                        <blockquote className="app-view-letter">{app.cover_letter}</blockquote>
                                    </section>
                                )}

                                <section className="app-view-section">
                                    <h3>{t('Documents')} <span>{documents.length}</span></h3>
                                    {documents.length ? (
                                        <ul className="app-view-docs">
                                            {documents.map((doc) => (
                                                <li key={doc.key}>
                                                    <button type="button" onClick={() => openDocument(doc.key, doc.name)}>
                                                        <span className="app-view-file" aria-hidden="true">{extension(doc.name) || 'DOC'}</span>
                                                        <span className="app-view-doc-text"><small>{doc.label}</small><b>{doc.name}</b></span>
                                                        <span className="app-view-open">{t('Open')}</span>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : <p className="app-view-empty">{t('No documents attached.')}</p>}
                                </section>

                                {(app.timeline || []).length > 0 && (
                                    <section className="app-view-section">
                                        <h3>{t('History')}</h3>
                                        <ol className="app-view-timeline">
                                            {app.timeline.map((item) => (
                                                <li key={item.id}>
                                                    <b>{t(statusLabels[item.status] || item.status)}</b>
                                                    <small>{formatDate(item.changed_at)}</small>
                                                    {item.notes && <p>{item.notes}</p>}
                                                </li>
                                            ))}
                                        </ol>
                                    </section>
                                )}
                            </div>

                            <footer className="app-view-footer">
                                {canReview && <label>
                                    <small>{t('Hali')}</small>
                                    <select className="status-select" value={app.status} onChange={(e) => changeViewingStatus(e.target.value)}>
                                        <option value="pending">{t('Pending')}</option>
                                        <option value="under_review">{t('Under Review')}</option>
                                        <option value="shortlisted">{t('Shortlisted')}</option>
                                        <option value="not_shortlisted">{t('Not Shortlisted')}</option>
                                        <option value="interviewed">{t('Interviewed')}</option>
                                        <option value="accepted">{t('Accepted')}</option>
                                        <option value="rejected">{t('Rejected')}</option>
                                    </select>
                                </label>}
                                <button type="button" className="btn btn-secondary" onClick={() => setViewingApplication(null)}>{t('Close')}</button>
                            </footer>
                        </div>
                    </div>
                );
            })()}

            {/* Job Form Modal */}
            {showJobForm && (
                <div className="modal-overlay" onClick={closeJobForm}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingJob ? t('Hariri Tangazo') : t('Tangazo Jipya')}</h2>
                            <button className="close-btn" onClick={closeJobForm}>×</button>
                        </div>
                        <form onSubmit={handleJobSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('Kichwa cha Kazi *')}</label>
                                    <input type="text" required value={jobForm.title} onChange={(e) => setJobForm({...jobForm, title: e.target.value})} />
                                </div>
                                <div className="form-group">
                                    <label>{t('Aina ya Ajira *')}</label>
                                    <select required value={jobForm.employment_type} onChange={(e) => setJobForm({...jobForm, employment_type: e.target.value})}>
                                        <option value="full_time">{t('Full Time')}</option>
                                        <option value="part_time">{t('Part Time')}</option>
                                        <option value="contract">{t('Contract')}</option>
                                        <option value="temporary">{t('Temporary')}</option>
                                        <option value="internship">{t('Internship')}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{t('Maelezo *')}</label>
                                <textarea required rows="4" value={jobForm.description} onChange={(e) => setJobForm({...jobForm, description: e.target.value})} />
                            </div>
                            <div className="form-group">
                                <label>{t('Mahitaji')}</label>
                                <textarea rows="3" value={jobForm.requirements} onChange={(e) => setJobForm({...jobForm, requirements: e.target.value})} />
                            </div>
                            <div className="form-group">
                                <label>{t('Majukumu')}</label>
                                <textarea rows="3" value={jobForm.responsibilities} onChange={(e) => setJobForm({...jobForm, responsibilities: e.target.value})} />
                            </div>
                            <div className="form-group">
                                <label>{t('Sifa')}</label>
                                <textarea rows="3" value={jobForm.qualifications} onChange={(e) => setJobForm({...jobForm, qualifications: e.target.value})} />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('Mahali')}</label>
                                    <input type="text" value={jobForm.location} onChange={(e) => setJobForm({...jobForm, location: e.target.value})} />
                                </div>
                                <div className="form-group">
                                    <label>{t('Mshahara')}</label>
                                    <input type="text" placeholder={t('e.g. 500,000 - 800,000 TSH')} value={jobForm.salary_range} onChange={(e) => setJobForm({...jobForm, salary_range: e.target.value})} />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('Tarehe ya Kutoa *')}</label>
                                    <input type="date" required value={jobForm.posted_date} onChange={(e) => setJobForm({...jobForm, posted_date: e.target.value})} />
                                </div>
                                <div className="form-group">
                                    <label>{t('Mwisho wa Maombi *')}</label>
                                    <input type="date" required value={jobForm.deadline} onChange={(e) => setJobForm({...jobForm, deadline: e.target.value})} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{t('Hali *')}</label>
                                <select required value={jobForm.status} onChange={(e) => setJobForm({...jobForm, status: e.target.value})}>
                                    <option value="draft">{t('Draft')}</option>
                                    <option value="open">{t('Open')}</option>
                                    <option value="closed">{t('Closed')}</option>
                                    <option value="cancelled">{t('Cancelled')}</option>
                                </select>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeJobForm}>{t('Ghairi')}</button>
                                <button type="submit" className="btn btn-primary">{t('Hifadhi')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Application Form Modal */}
            {showApplicationForm && (
                <div className="modal-overlay" onClick={closeApplicationForm}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{t('Omba Kazi')}</h2>
                            <button className="close-btn" onClick={closeApplicationForm}>×</button>
                        </div>
                        <form onSubmit={handleApplicationSubmit}>
                            <div className="form-group">
                                <label>{t('Chagua Tangazo *')}</label>
                                <select required value={applicationForm.job_posting_id} onChange={(e) => setApplicationForm({...applicationForm, job_posting_id: e.target.value})}>
                                    <option value="">{t('-- Chagua --')}</option>
                                    {jobs.filter(j => j.status === 'open').map(job => (
                                        <option key={job.id} value={job.id}>{job.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>{t('Jina Kamili *')}</label>
                                <input type="text" required value={applicationForm.applicant_name} onChange={(e) => setApplicationForm({...applicationForm, applicant_name: e.target.value})} />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Email *</label>
                                    <input type="email" required value={applicationForm.applicant_email} onChange={(e) => setApplicationForm({...applicationForm, applicant_email: e.target.value})} />
                                </div>
                                <div className="form-group">
                                    <label>{t('Simu *')}</label>
                                    <input type="tel" required value={applicationForm.applicant_phone} onChange={(e) => setApplicationForm({...applicationForm, applicant_phone: e.target.value})} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{t('Cover Letter')}</label>
                                <textarea rows="4" value={applicationForm.cover_letter} onChange={(e) => setApplicationForm({...applicationForm, cover_letter: e.target.value})} placeholder={t('Andika maelezo kuhusu wewe na kwa nini unafaa kwa kazi hii...')} />
                            </div>
                            <div className="form-group">
                                <label>{t('Resume/CV')}</label>
                                <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setApplicationForm({...applicationForm, resume: e.target.files[0]})} />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeApplicationForm}>{t('Ghairi')}</button>
                                <button type="submit" className="btn btn-primary">{t('Tuma Ombi')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Recruitment;
